import React from 'react'
import { Modal, StyleSheet, View } from 'react-native'
import { DS } from '@/design-system'
import { getDSColorWithOpacity } from '@/design-system/Palette'
import ConfettiCelebration from '@/components/Mini/ConfettiCelebration'

interface Props {
  visible: boolean
  /** Winner display name; null while the status query is loading. */
  winnerName: string | null
  /** True when the viewer is the winner — switches copy to second person. */
  isWinner: boolean
  tournamentName?: string
  onViewResults: () => void
  onClose: () => void
}

/**
 * Winner announcement modal shown when a tournament finishes: crown mark,
 * falling confetti (same component as the minigame results screens), and the
 * winner's name. Dismissible — unlike StickyDecisionModal there is no
 * decision to force, so hardware back and the Close button both dismiss.
 */
export function TournamentWinnerModal({
  visible,
  winnerName,
  isWinner,
  tournamentName,
  onViewResults,
  onClose
}: Props) {
  if (!visible) return null

  const title = isWinner ? 'You Won the Tournament!' : 'Tournament Complete'
  const body = isWinner
    ? `Congratulations${tournamentName ? `, champion of ${tournamentName}` : ''}!`
    : `${winnerName ?? 'The winner'} takes it down${tournamentName ? ` in ${tournamentName}` : ''}!`

  return (
    <Modal visible transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.backdrop}>
        <ConfettiCelebration />
        <View style={styles.card}>
          <DS.Icon name="Crown" width={48} height={48} color="Brand/Maroon" />
          <DS.Text variant="Heading 2/Medium" textAlign="center">
            {title}
          </DS.Text>
          <DS.Text variant="Body Large/Regular" color="Brand/Dark Grey" textAlign="center">
            {body}
          </DS.Text>
          <DS.Frame gap="8" style={styles.buttons}>
            <DS.Button type="On Light" variant="Large/Primary" backgroundColor="Brand/Maroon" onPress={onViewResults}>
              View Results
            </DS.Button>
            <DS.Button type="On Light" variant="Large/Secondary" backgroundColor="Global/Black" onPress={onClose}>
              Close
            </DS.Button>
          </DS.Frame>
        </View>
      </View>
    </Modal>
  )
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: getDSColorWithOpacity('Global/Black', 0.5),
    justifyContent: 'center',
    alignItems: 'center'
  },
  card: {
    backgroundColor: DS.palette['Brand/Cream'],
    borderRadius: 16,
    padding: 24,
    alignItems: 'center',
    gap: 12,
    width: '100%',
    maxWidth: 340
  },
  buttons: {
    width: '100%',
    marginTop: 8
  }
})
